import { useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { Briefcase, RefreshCw, X } from "lucide-react";
import type { Job } from "@repo/shared";
import { jobTypeSchema, jobStatusSchema } from "@repo/shared";
import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  DateRangeInput,
  Field,
  FilterChip,
  Mono,
  PageContent,
  PageHeader,
  PageShell,
  Progress,
  StatusBadge,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
  type StatusBadgeProps,
} from "@h2j-data-team/ui";

import { useLocale } from "@/hooks/use-locale";
import { useFormatDate } from "@/hooks/use-format-date";
import { DensityToggle } from "@/components/density-toggle";
import { supportCode } from "@/components/error-screen";
import { useTableDensity } from "@/lib/use-table-density";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { jobsPageQueryOptions } from "@/features/jobs/api";
import { makeJobTypeLabeler } from "@/features/jobs/labels";
import { describeJobScope } from "@/features/jobs/scope";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";
import { groupsQueryOptions } from "@/features/groups/api";

const DEFAULT_ROWS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 300;

type JobsSearch = {
  type?: string;
  status?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
};

/** What the toolbar controls; "all" is `undefined`, never an empty list. */
type JobsFilters = {
  type?: string[];
  status?: string[];
  dateFrom?: string;
  dateTo?: string;
};

type StatusTone = NonNullable<StatusBadgeProps["tone"]>;

const STATUS_TONE: Record<Job["status"], StatusTone> = {
  pending: "info",
  processing: "warning",
  completed: "success",
  failed: "danger",
};

// Callers should pass job.startedAt ?? job.createdAt as `start` to match the
// job detail page's duration (completedAt - startedAt), excluding queue wait time.
function formatDuration(start: string, end: string | null | undefined): string {
  if (!end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const totalMinutes = Math.floor(s / 60);
  const seconds = s % 60;
  if (totalMinutes < 60) return `${totalMinutes}m ${seconds}s`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
}

function csvToList(csv: string | undefined): string[] | undefined {
  const list = (csv ?? "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
  return list.length > 0 ? list : undefined;
}

function listToCsv(list: string[] | undefined): string | undefined {
  return list && list.length > 0 ? list.join(",") : undefined;
}

/** A toolbar chip whose popover is a list of checkboxes: "Status: Failed, +1". */
function OptionsFilter({
  label,
  clearLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  clearLabel: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const labels = value.map((v) => options.find((o) => o.value === v)?.label ?? v);
  const summary =
    labels.length === 0
      ? undefined
      : labels.length === 1
        ? labels[0]
        : `${labels[0]}, +${labels.length - 1}`;

  return (
    <FilterChip
      label={label}
      summary={summary}
      clearLabel={clearLabel}
      onClear={() => onChange([])}
    >
      <div role="group" aria-label={label} className="flex flex-col gap-2">
        {options.map((option) => (
          <Field key={option.value} inline label={option.label}>
            <Checkbox
              checked={value.includes(option.value)}
              onCheckedChange={(checked) =>
                onChange(
                  checked === true
                    ? [...value, option.value]
                    : value.filter((v) => v !== option.value),
                )
              }
            />
          </Field>
        ))}
      </div>
    </FilterChip>
  );
}

export const Route = createFileRoute("/_authenticated/jobs/")({
  validateSearch: (search: Record<string, unknown>): JobsSearch => ({
    type: typeof search.type === "string" ? search.type : undefined,
    status: typeof search.status === "string" ? search.status : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
    dateFrom: typeof search.dateFrom === "string" ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === "string" ? search.dateTo : undefined,
  }),
  component: JobsPage,
});

function JobsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation("jobs");
  const { t: tCommon } = useTranslation("common");
  const { dateFnsLocale } = useLocale();
  const { formatDateTime } = useFormatDate();
  const { density, setDensity } = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  const jobTypeLabel = useMemo(() => makeJobTypeLabeler(t), [t]);
  const jobTypeOptions = useMemo(
    () => jobTypeSchema.options.map((type) => ({ value: type, label: jobTypeLabel(type) })),
    [jobTypeLabel],
  );
  const jobStatusOptions = useMemo(
    () => jobStatusSchema.options.map((s) => ({ value: s, label: t(`status.${s}`) })),
    [t],
  );

  const { type, status, q, dateFrom, dateTo } = search;

  // The page and its size are display state, not URL: only the filters are shared links.
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const state: DataTableState<JobsFilters> = {
    page,
    pageSize: rowsPerPage,
    query: q ?? "",
    filters: { type: csvToList(type), status: csvToList(status), dateFrom, dateTo },
    selection: [],
  };

  // Filters, search and pagination are all server-side, so `total` spans the whole
  // job history rather than a fetched window.
  const jobsQuery = useQuery(
    jobsPageQueryOptions({
      page,
      pageSize: rowsPerPage,
      type,
      status,
      q,
      dateFrom,
      dateTo,
    }),
  );
  const jobs = jobsQuery.data?.data ?? [];
  const total = jobsQuery.data?.total ?? 0;

  // Names for the scope column: payloads carry ids, not display names. Both
  // lists are already cached by other pages, so this is usually a cache hit.
  const { data: connections } = useQuery(connectionsQueryOptions);
  const { data: groupsData } = useQuery(groupsQueryOptions());
  const connectionNames = useMemo(
    () => new Map((connections ?? []).map((c) => [c.id, c.name])),
    [connections],
  );
  const groupNames = useMemo(
    () => new Map((groupsData?.data ?? []).map((g) => [g.id, g.name])),
    [groupsData],
  );

  // Every change of the table or its toolbar lands here. The filters live in the
  // URL (replace, so they don't stack history); the table already sent the page
  // back to 1 when the scope changed, which a page past the new result set needs.
  const onStateChange = (next: DataTableState<JobsFilters>) => {
    setPage(next.page);
    setRowsPerPage(next.pageSize);
    const scopeChanged =
      next.query !== state.query || JSON.stringify(next.filters) !== JSON.stringify(state.filters);
    if (!scopeChanged) return;
    navigate({
      search: (prev) => ({
        ...prev,
        q: next.query || undefined,
        type: listToCsv(next.filters.type),
        status: listToCsv(next.filters.status),
        dateFrom: next.filters.dateFrom || undefined,
        dateTo: next.filters.dateTo || undefined,
      }),
      replace: true,
    });
  };

  const patchFilters = (patch: Partial<JobsFilters>) =>
    onStateChange(updateTableState(state, { filters: { ...state.filters, ...patch } }));

  const hasActiveFilters = !!type || !!status || !!q || !!dateFrom || !!dateTo;
  const clearFilters = () => onStateChange(updateTableState(state, { query: "", filters: {} }));

  const columns = useMemo<DataTableColumn<Job>[]>(
    () => [
      {
        id: "type",
        header: t("table.type"),
        // Fits the longest type label ("ANÁLISE DE ARMAZENAMENTO") on one line.
        width: 230,
        rowHeader: true,
        // The row opens the job through this link (a click anywhere else on the
        // row is forwarded to it).
        rowLink: true,
        cell: (job) => {
          const label = jobTypeLabel(job.type);
          return (
            <Badge
              asChild
              variant="secondary"
              className="font-mono text-[10.5px] uppercase tracking-[0.04em] hover:underline"
            >
              <Link
                to="/jobs/$id"
                params={{ id: job.id }}
                aria-label={t("list.openJob", {
                  type: label,
                  time: formatDistanceToNow(new Date(job.createdAt), {
                    addSuffix: true,
                    locale: dateFnsLocale,
                  }),
                })}
              >
                {label}
              </Link>
            </Badge>
          );
        },
      },
      {
        id: "scope",
        header: t("table.scope"),
        width: 260,
        cell: (job) => {
          const segments = describeJobScope(job, {
            connectionName: (id) => connectionNames.get(id),
            groupName: (id) => groupNames.get(id),
            t,
          });
          if (segments.length === 0) return <span className="text-dim">—</span>;
          return (
            <span className="text-[12.5px]" title={segments.join(" · ")}>
              {segments.map((segment, i) => (
                <span key={i}>
                  {i > 0 && <span className="text-faint"> · </span>}
                  <span className={i === 0 ? "text-dim" : "text-ink"}>{segment}</span>
                </span>
              ))}
            </span>
          );
        },
      },
      {
        id: "status",
        header: t("table.status"),
        width: 150,
        cell: (job) => (
          <StatusBadge tone={STATUS_TONE[job.status]}>{t(`status.${job.status}`)}</StatusBadge>
        ),
      },
      {
        id: "result",
        header: t("table.result"),
        cell: (job) => {
          const progress = job.progress;
          if (job.status === "processing" && progress) {
            const percent =
              progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
            return (
              <span className="flex items-center gap-2">
                <Progress value={percent} className="w-24" aria-hidden />
                <span className="font-mono text-[12px] text-dim">{percent}%</span>
              </span>
            );
          }
          if (job.result) {
            return (
              <span className="flex gap-3 text-[12.5px]">
                <span className="text-status-success-foreground">
                  {t("card.succeeded", { count: job.result.summary.succeeded })}
                </span>
                {job.result.summary.failed > 0 && (
                  <span className="text-status-danger-foreground">
                    {t("card.failed", { count: job.result.summary.failed })}
                  </span>
                )}
              </span>
            );
          }
          return <span className="text-dim">—</span>;
        },
      },
      {
        id: "duration",
        header: t("table.duration"),
        width: 120,
        variant: "numeric",
        cell: (job) => formatDuration(job.startedAt ?? job.createdAt, job.completedAt),
      },
      {
        id: "created",
        header: t("table.created"),
        width: 170,
        cell: (job) => (
          <time dateTime={job.createdAt} title={formatDateTime(job.createdAt)}>
            {formatDistanceToNow(new Date(job.createdAt), {
              addSuffix: true,
              locale: dateFnsLocale,
            })}
          </time>
        ),
      },
    ],
    [t, jobTypeLabel, connectionNames, groupNames, dateFnsLocale, formatDateTime],
  );

  const code = supportCode(jobsQuery.error, tCommon("error.supportCode"));

  return (
    <PageShell
      header={
        <PageHeader
          title={t("list.pageTitle")}
          description={t("list.description")}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={() => void jobsQuery.refetch()}
              loading={jobsQuery.isFetching}
            >
              <RefreshCw />
              {t("list.refresh")}
            </Button>
          }
        />
      }
    >
      <PageContent fill>
        <DataTable
          label={t("list.pageTitle")}
          columns={columns}
          rows={jobs}
          getRowId={(job) => job.id}
          state={state}
          onStateChange={onStateChange}
          density={density}
          loading={jobsQuery.isPending}
          // Polling and the Refresh button stay silent; only a page or filter change
          // still showing the previous rows, or a retry, is "updating".
          refreshing={listRefreshing(jobsQuery)}
          error={
            jobsQuery.isError
              ? {
                  title: tCommon("error.loadFailed"),
                  description: code ? (
                    <>
                      {code.label}: <Mono>{code.value}</Mono>
                    </>
                  ) : undefined,
                }
              : null
          }
          onRetry={() => void jobsQuery.refetch()}
          messages={tableMessages({
            empty: {
              title: t("list.empty.noJobs"),
              description: t("list.empty.noJobsDescription"),
              icon: <Briefcase />,
            },
            noResults: {
              title: t("list.empty.noMatch"),
              description: t("list.empty.noMatchDescription"),
              action: (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  {t("list.clearFilters")}
                </Button>
              ),
            },
          })}
          pagination={{
            kind: "offset",
            total,
            pageSizeOptions: [25, 50, 100],
            labels: paginationLabels(),
          }}
          search={{
            ...tableSearch(t("list.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
            debounceMs: SEARCH_DEBOUNCE_MS,
          }}
          toolbar={
            <>
              <OptionsFilter
                label={t("table.type")}
                clearLabel={t("list.clearType")}
                options={jobTypeOptions}
                value={state.filters.type ?? []}
                onChange={(value) => patchFilters({ type: value.length > 0 ? value : undefined })}
              />
              <OptionsFilter
                label={t("table.status")}
                clearLabel={t("list.clearStatus")}
                options={jobStatusOptions}
                value={state.filters.status ?? []}
                onChange={(value) => patchFilters({ status: value.length > 0 ? value : undefined })}
              />
              <DateRangeInput
                size="compact"
                aria-label={t("list.filterPeriod")}
                value={{ from: dateFrom, to: dateTo }}
                onValueChange={({ from, to }) =>
                  patchFilters({ dateFrom: from || undefined, dateTo: to || undefined })
                }
                fromLabel={t("list.dateFrom")}
                toLabel={t("list.dateTo")}
                clearLabel={t("list.clearPeriod")}
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="compact" onClick={clearFilters}>
                  <X />
                  {t("list.clearFilters")}
                </Button>
              )}
              <div className="ml-auto">
                <DensityToggle value={density} onChange={setDensity} />
              </div>
            </>
          }
        />
      </PageContent>
    </PageShell>
  );
}
